import React, { useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Space,
  Alert,
  message,
  Row,
  Col,
  Divider,
  Tag
} from 'antd';
import {
  SendOutlined,
  ClockCircleOutlined,
  UserOutlined,
  MessageOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  HeartOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface ReminderConfig {
  id: string;
  recipientName: string;
  reminderDate: string;
  reminderTime: string;
  reminderContent: string;
  aiPreview: string;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  createdAt: string;
  scheduledAt?: string;
  sentAt?: string;
}

interface ReminderConfigPanelProps {
  onAddReminder: (reminder: ReminderConfig) => void;
  currentReminder: Partial<ReminderConfig>;
  onUpdateCurrentReminder: (reminder: Partial<ReminderConfig>) => void;
  isWechatConfigured: boolean;
}

const ReminderConfigPanel: React.FC<ReminderConfigPanelProps> = ({
  onAddReminder,
  currentReminder,
  onUpdateCurrentReminder,
  isWechatConfigured
}) => {
  const [form] = Form.useForm();
  const [previewing, setPreviewing] = useState(false);
  const [aiPreview, setAiPreview] = useState('');

  // 如果微信未配置
  if (!isWechatConfigured) {
    return (
      <Card style={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        textAlign: 'center',
        padding: '60px 40px'
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px',
          fontSize: '48px',
          color: 'white'
        }}>
          <MessageOutlined />
        </div>

        <Title level={3} style={{ color: '#ff6b6b', marginBottom: '15px' }}>
          请先配置微信机器人
        </Title>

        <Paragraph type="secondary" style={{ marginBottom: '30px' }}>
          设置暖心提醒之前，需要先绑定微信机器人
        </Paragraph>

        <Alert
          message="需要配置微信"
          description="请先在「微信配置」页面完成机器人绑定，然后再回来设置提醒"
          type="warning"
          showIcon
          style={{ maxWidth: '400px', margin: '0 auto' }}
        />
      </Card>
    );
  }

  // 生成AI预览
  const generatePreview = async () => {
    try {
      const values = await form.validateFields();
      setPreviewing(true);

      // 模拟API调用生成AI预览
      setTimeout(() => {
        const previewText = `💌 来自寄语信箱的暖心提醒

亲爱的${values.recipientName}：

${values.reminderContent}

这是我在 ${values.reminderDate?.format('YYYY年MM月DD日')}
${values.reminderTime?.format('HH:mm')} 定时发送的提醒

愿这份心意能温暖你的心 💕`;

        setAiPreview(previewText);
        setPreviewing(false);
        message.success('✅ AI预览已生成');
      }, 1500);
    } catch (error) {
      message.error('请先填写必要信息');
    }
  };

  // 提交提醒
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!aiPreview) {
        message.warning('请先生成预览再提交');
        return;
      }

      const reminder: ReminderConfig = {
        id: `reminder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        recipientName: values.recipientName,
        reminderDate: values.reminderDate?.format('YYYY-MM-DD') || '',
        reminderTime: values.reminderTime?.format('HH:mm') || '',
        reminderContent: values.reminderContent,
        aiPreview,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        scheduledAt: `${values.reminderDate?.format('YYYY-MM-DD')}T${values.reminderTime?.format('HH:mm')}:00`
      };

      onAddReminder(reminder);

      // 重置表单
      form.resetFields();
      setAiPreview('');
    } catch (error) {
      message.error('请填写所有必要信息');
    }
  };

  return (
    <Card style={{
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      border: '1px solid #fff1f0'
    }}>
      <Title level={3} style={{ color: '#ff6b6b', marginBottom: '10px' }}>
        <HeartOutlined /> 提醒内容配置
      </Title>

      <Paragraph type="secondary" style={{ marginBottom: '30px' }}>
        填写收信人信息、提醒时间和内容，AI将帮你生成暖心的提醒消息
      </Paragraph>

      <Row gutter={[30, 30]}>
        <Col xs={24} lg={12}>
          <div style={{
            background: 'linear-gradient(135deg, #fff5f5, #f0f9ff)',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #ffcccc'
          }}>
            <Title level={4} style={{ marginBottom: '20px', color: '#ff6b6b' }}>
              <UserOutlined /> 填写提醒信息
            </Title>

            <Form
              form={form}
              layout="vertical"
              size="large"
            >
              <Form.Item
                label="收信方称呼"
                name="recipientName"
                rules={[{ required: true, message: '请输入收信方的称呼' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="例如：妈妈、亲爱的、王总"
                  maxLength={20}
                  showCount
                />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="提醒日期"
                    name="reminderDate"
                    rules={[{ required: true, message: '请选择提醒日期' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="选择日期"
                      format="YYYY-MM-DD"
                      disabledDate={(current) => {
                        return current && current < dayjs().startOf('day');
                      }}
                      suffixIcon={<ClockCircleOutlined />}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="提醒时间"
                    name="reminderTime"
                    rules={[{ required: true, message: '请选择提醒时间' }]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      placeholder="选择时间"
                      format="HH:mm"
                      suffixIcon={<ClockCircleOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="提醒内容"
                name="reminderContent"
                rules={[
                  { required: true, message: '请输入提醒内容' },
                  { min: 10, message: '提醒内容至少10个字' }
                ]}
              >
                <TextArea
                  placeholder="写下你想要提醒的内容，例如：提醒妈妈记得按时吃药，明天下午3点有重要会议等..."
                  rows={4}
                  maxLength={200}
                  showCount
                />
              </Form.Item>

              <Divider />

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Button
                    block
                    size="large"
                    icon={<EyeOutlined />}
                    onClick={generatePreview}
                    loading={previewing}
                    type="default"
                  >
                    生成预览
                  </Button>
                </Col>

                <Col span={12}>
                  <Button
                    type="primary"
                    block
                    size="large"
                    icon={<SendOutlined />}
                    onClick={handleSubmit}
                    disabled={!aiPreview}
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
                      borderColor: '#ff6b6b'
                    }}
                  >
                    提交提醒
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div style={{
            background: '#f9f9f9',
            padding: '24px',
            borderRadius: '12px',
            height: '100%',
            minHeight: '400px'
          }}>
            <Title level={4} style={{ marginBottom: '20px', color: '#52c41a' }}>
              <CheckCircleOutlined /> AI预览效果
            </Title>

            {aiPreview ? (
              <>
                <div style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                  marginBottom: '20px',
                  minHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  fontSize: '14px',
                  color: '#333'
                }}>
                  {aiPreview}
                </div>

                <Space wrap>
                  <Tag color="green">AI生成</Tag>
                  <Tag color="blue">暖心风格</Tag>
                  <Tag color="purple">定时发送</Tag>
                </Space>

                <Alert
                  message="预览满意了吗？"
                  description="如果预览效果满意，点击「提交提醒」按钮完成设置"
                  type="info"
                  showIcon
                  style={{ marginTop: '20px' }}
                />
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#999'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>
                  <MessageOutlined />
                </div>
                <Paragraph>
                  填写完信息后，点击「生成预览」按钮<br />
                  AI将为你生成暖心的提醒消息
                </Paragraph>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Divider style={{ marginTop: '30px' }} />

      <Alert
        message="温馨提示"
        description="提醒消息将通过微信机器人发送，请确保收信方已经添加了机器人为好友。提醒将在设定的时间准时发送。"
        type="info"
        showIcon
        style={{ borderRadius: '8px' }}
      />
    </Card>
  );
};

export default ReminderConfigPanel;
